import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalFooter, ModalBody, Alert, Label, Button, ModalHeader } from 'reactstrap';
import * as history from 'history';
import entityComposition from '../common/classes/entityComposition';
import SinglePicker from '../common/controls/singlePicker';
import * as ajax from '../../actions/ajax';
import Loader from '../common/loader';
import store from '../../reducers/storeRegistration';
import loginActions from '../../actions/loginActionCreators';
const hashHistory = history.createHashHistory();
const IssueExportModal = ({
  reportTypeID: _reportTypeID = undefined,
  reportIssueBulkExport: {
    list: reportsList,
    indexQuery: reportIndexQuery
  },
  reportIssueBulkExportActions: {
    serverFilter: reportActionsServerFilter
  },
  jobIssueBulkExport: {
    list: jobsList,
    indexQuery: jobIndexQuery
  },
  jobIssueBulkExportActions: {
    serverFilter: jobServerFilter
  },
  createNewReport: _createNewReport = false,
  selected: selectedIssues = [],
  isOpen: _isOpen = false,
  toggle
}) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [exportingIssues, setExportingIssues] = useState(false);
  const [exportingErrorMessage, setExportingErrorMessage] = useState(null);

  // load job picker options (if open and creating new report)
  useEffect(() => {
    if (_isOpen && _createNewReport) jobServerFilter({}, jobIndexQuery);
  }, [_createNewReport, jobServerFilter, jobIndexQuery, _isOpen]);

  // load report picker options (if open and not creating new report)
  useEffect(() => {
    if (_isOpen && !_createNewReport) reportActionsServerFilter({
      reportTypeIdentifier: 'issueExportReport'
    }, reportIndexQuery);
  }, [reportActionsServerFilter, reportIndexQuery, _createNewReport, _isOpen]);
  const dismissModal = () => {
    if (_createNewReport) setSelectedJob(null);
    if (!_createNewReport) setSelectedReport(null);
    setExportingErrorMessage(false);
    setExportingIssues(false);
    toggle();
  };
  const buttonDisabled = _createNewReport ? selectedJob === null : selectedReport === null;
  const handleIssueExport = async () => {
    // setup the targets based on selections
    const target = _createNewReport ? {
      jobID: selectedJob
    } : {
      reportID: selectedReport
    };
    target.reportTypeID = _reportTypeID;

    // start the loading indicator and kick off the request
    setExportingIssues(true);
    try {
      const {
        data: {
          reportID,
          error
        }
      } = await ajax.post('/issues/export', {
        target,
        issues: selectedIssues
      });
      setExportingIssues(false);

      // set error to state for display
      if (error) setExportingErrorMessage(error);

      // redirect to the reportID of choice
      if (reportID) {
        dismissModal();
        hashHistory.push(`/reports/${reportID}`);
      }
    } catch (e) {
      var _e$response;
      // If token expired (401), refresh and retry once
      if (((_e$response = e.response) === null || _e$response === void 0 ? void 0 : _e$response.status) === 401) {
        try {
          await store.dispatch(loginActions.serverToken());
          const {
            data: {
              reportID,
              error
            }
          } = await ajax.post('/issues/export', {
            target,
            issues: selectedIssues
          });
          setExportingIssues(false);

          // set error to state for display
          if (error) setExportingErrorMessage(error);

          // redirect to the reportID of choice
          if (reportID) {
            dismissModal();
            hashHistory.push(`/reports/${reportID}`);
          }
          return;
        } catch (_unused) {
          // Retry failed, fall through to error handling
        }
      }
      setExportingIssues(false);
      setExportingErrorMessage('Failed to export issues. Please try again.');
    }
  };
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: _isOpen,
    toggle: () => dismissModal()
  }, /*#__PURE__*/React.createElement(ModalHeader, null, "Export issues"), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Label, null, "Select a", _createNewReport ? ' Job to create the new Issue Export Report under' : 'n existing Issue Export Report'), _createNewReport ? /*#__PURE__*/React.createElement(SinglePicker, {
    disabled: exportingIssues,
    id: "add-select-job",
    className: "t-s-job",
    options: jobsList.map(job => {
      const jobOption = {
        name: job.jobNumber,
        id: job.id
      };
      if (job.name) jobOption.subtitle = job.name;
      return jobOption;
    }),
    onChange: job => {
      setSelectedJob(job ? jobsList.find(r => r.id === job.id) : null);
    },
    placeholder: "Required",
    value: selectedJob
  }) : /*#__PURE__*/React.createElement(SinglePicker, {
    disabled: exportingIssues,
    id: "add-select-report",
    className: "t-s-report",
    options: reportsList.map(report => ({
      name: `${report.reportTypeName} ${report.reportNumber}`,
      id: report.id,
      subtitle: `${report.jobNumber} - ${report.appCreated}`
    })),
    onChange: report => {
      setSelectedReport(report ? reportsList.find(r => r.id === report.id) : null);
    },
    placeholder: "Required",
    value: selectedReport
  }), /*#__PURE__*/React.createElement(Loader, {
    loaded: !exportingIssues
  }), /*#__PURE__*/React.createElement(Alert, {
    color: "danger",
    isOpen: exportingErrorMessage
  }, exportingErrorMessage)), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    onClick: dismissModal,
    className: "t-iem-cancel",
    disabled: exportingIssues
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    disabled: buttonDisabled || exportingIssues,
    className: "t-iem-assign",
    onClick: handleIssueExport
  }, _createNewReport ? 'Create new report' : 'Add issues to existing report')));
};
IssueExportModal.propTypes = {
  createNewReport: PropTypes.bool,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  reportIssueBulkExport: PropTypes.shape({
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({})
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  reportIssueBulkExportActions: PropTypes.shape({
    serverFilter: PropTypes.func
  }).isRequired,
  jobIssueBulkExport: PropTypes.shape({
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({})
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  jobIssueBulkExportActions: PropTypes.shape({
    serverFilter: PropTypes.func
  }).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string),
  reportTypeID: PropTypes.string
};
export { IssueExportModal as TestIssueExportModal };
export default entityComposition(IssueExportModal, ['reportIssueBulkExport', 'jobIssueBulkExport']);